// @flow
import * as React from 'react';
import { Device } from '@ta/lithium.context';
// polyfill library
import { Sticky as StickyPolyfill, StickyContainer as StickyContainerPolyfill } from 'react-sticky';
import styles from './Sticky.css';

type Props = {|
  /** how far from the top of the container should the element become sticky */
  offsetTop?: number,

  /** how far from the bottom of the container should the element become sticky */
  offsetBottom?: number,

  /** z index override */
  zIndex?: number,

  /** component that will be stick */
  stickyElement: React.Node,

  /** additional class name for the container */
  containerClassName?: string,

  /**
   * A component used to retain the sticky element's width when position: fixed in the polyfill.
   */
  polyfillStickyElementSpacer?: React.Node,

  /** Whether to disable hardware acceleration of the Sticky polyfill */
  disableHardwareAcceleration?: boolean,

  /** [non-sticky] contents of the container */
  children?: React.Node,

  /** Whether to append the sticky element to the children */
  append?: boolean,
|};

/**
 * Warning: react-sticky is abandoned and may not work as expected.  Since the fallback is only for IE,
 * you may want to consider using the native position: sticky and omitting that behavior from IE altogether.
 *
 * Sticky position component that allows the behavior of position: sticky for all browsers.
 * For performance, native position: sticky will be used on browsers that support it and a
 * javascript fallback will be used for browsers that don't support it (IE)
 *
 * The fallback makes use of position: fixed.  This takes the sticky element out of layout.  If you have siblings that
 * depend on the size of the sticky element for layout, you should also set polyfillStickyElementSpacer to an element
 * with the equivalent size as the sticky element itself.
 */
const Sticky = ({
  stickyElement,
  children,
  offsetTop = 0,
  offsetBottom = 0,
  zIndex = 1,
  containerClassName = '',
  polyfillStickyElementSpacer,
  disableHardwareAcceleration = false,
  append,
}: Props) => {
  const { browser } = React.useContext(Device);
  const [polyfillSticky, setPolyfillSticky] = React.useState(false);
  if (browser.family === 'IE') {
    return (
      <StickyContainerPolyfill className={containerClassName}>
        {append && children}
        <StickyPolyfill
          topOffset={-1 * offsetTop}
          bottomOffset={offsetBottom}
          disableHardwareAcceleration={disableHardwareAcceleration}
        >
          {({ style, isSticky }) => {
            setPolyfillSticky(isSticky);
            return (
              <div
                style={{
                  ...style,
                  paddingTop: isSticky ? offsetTop : 0,
                  zIndex,
                }}
              >
                {stickyElement}
              </div>
            );
          }}
        </StickyPolyfill>
        {polyfillSticky && polyfillStickyElementSpacer}
        {!append && children}
      </StickyContainerPolyfill>
    );
  }
  return (
    <div className={`${styles.sticky_container} ${containerClassName || ''}`}>
      {append && children}
      <div className={styles.sticky_element} style={{ top: offsetTop, zIndex }}>
        {stickyElement}
      </div>
      {!append && children}
    </div>
  );
};

Sticky.defaultProps = {
  offsetTop: 0,
  offsetBottom: 0,
  zIndex: 1,
  containerClassName: '',
  polyfillStickyElementSpacer: undefined,
  disableHardwareAcceleration: false,
};

export default Sticky;
